/**
 * 클라이언트 컴포넌트용 Feature Flags 유틸리티
 *
 * @description
 * 클라이언트 컴포넌트에서 Feature Flag를 사용하기 위한 래퍼.
 * @unleash/nextjs/client의 useFlag를 직접 사용해도 되지만,
 * 로딩 상태 처리와 일관된 API를 제공하기 위해 래퍼를 만듦.
 *
 * 왜 래퍼가 필요한가?
 * 1. useFlag()만 쓰면 flagsReady 체크를 매번 해야 함
 * 2. 로딩/에러/성공 상태를 일관되게 처리하고 싶음
 * 3. 프로젝트 전체에서 동일한 패턴으로 사용하고 싶음
 *
 * 제공하는 것:
 * - FeatureGuard: 선언적 컴포넌트 (children/fallback 패턴)
 * - useFeatureFlag: 훅 (isEnabled/isLoading 반환)
 *
 * 데이터 흐름:
 * 1. FlagProvider (providers/index.tsx)가 /api/features 폴링 (15초)
 * 2. 응답이 오면 내부 상태 업데이트 (flagsReady = true)
 * 3. useFlag()가 해당 플래그의 ON/OFF 반환
 * 4. useFlagsStatus()로 로딩 상태 확인 가능
 */

'use client';

import { useFlag, useFlagsStatus } from '@unleash/nextjs/client';
import type { ReactNode } from 'react';

/**
 * FeatureGuard Props
 *
 * 선언적으로 Feature Flag 분기를 처리할 때 사용.
 * 조건부 렌더링을 깔끔하게 표현 가능.
 */
interface FeatureGuardProps {
  /** Feature flag 이름 */
  flag: string;
  /** 플래그가 ON일 때 렌더링할 컨텐츠 */
  children: ReactNode;
  /** 플래그가 OFF일 때 렌더링할 컨텐츠 (기본: null) */
  fallback?: ReactNode;
  /** 플래그 로딩 중 렌더링할 컨텐츠 (기본: null) */
  loading?: ReactNode;
}

/**
 * Feature Flag 가드 컴포넌트
 *
 * 선언적으로 Feature Flag 분기를 처리.
 * if문 대신 컴포넌트로 조건부 렌더링을 표현할 수 있음.
 *
 * 렌더링 로직:
 * 1. flagsReady === false → loading 렌더링 (기본: null)
 * 2. isEnabled === true → children 렌더링
 * 3. isEnabled === false → fallback 렌더링 (기본: null)
 *
 * @example 신규 기능 ON/OFF - 플래그 OFF면 렌더링 안함
 * <FeatureGuard flag="new-checkout">
 *   <NewCheckout />
 * </FeatureGuard>
 *
 * @example A/B 테스트 - 플래그 OFF면 기존 UI 표시
 * <FeatureGuard flag="new-checkout" fallback={<OldCheckout />}>
 *   <NewCheckout />
 * </FeatureGuard>
 *
 * @example 로딩 처리 - API 응답 전까지 스켈레톤 표시
 * <FeatureGuard flag="beta-feature" loading={<Skeleton />}>
 *   <BetaFeature />
 * </FeatureGuard>
 */
export function FeatureGuard({ flag, children, fallback = null, loading = null }: FeatureGuardProps) {
  const { flagsReady } = useFlagsStatus();
  const isEnabled = useFlag(flag);

  if (!flagsReady) {
    return <>{loading}</>;
  }

  return <>{isEnabled ? children : fallback}</>;
}

/**
 * useFeatureFlag 반환 타입
 *
 * React Query의 useQuery 패턴을 참고해서 설계.
 * isLoading, isEnabled 등 상태를 명확하게 구분.
 */
interface UseFeatureFlagResult {
  /** 플래그 활성화 여부 (로딩 중이면 false) */
  isEnabled: boolean;
  /** 플래그 로딩 중 여부 (첫 API 응답 전까지 true) */
  isLoading: boolean;
  /** 플래그 준비 완료 여부 (API 응답 받으면 true) */
  isReady: boolean;
}

/**
 * Feature Flag 커스텀 훅
 *
 * FeatureGuard보다 유연한 제어가 필요할 때 사용.
 * useFlag()와 달리 로딩 상태도 함께 반환.
 *
 * @example 커스텀 로딩/에러 처리 - 세밀한 상태 제어 필요시
 * const { isEnabled, isLoading } = useFeatureFlag('my-feature');
 * if (isLoading) return <Skeleton />;
 * if (!isEnabled) return null;
 */
export function useFeatureFlag(flagName: string): UseFeatureFlagResult {
  const { flagsReady } = useFlagsStatus();
  const isEnabled = useFlag(flagName);

  return {
    isEnabled: flagsReady ? isEnabled : false,
    isLoading: !flagsReady,
    isReady: flagsReady,
  };
}
